import { useCallback, useEffect, useRef } from 'react';

const DEFAULT_THRESHOLD_MS = 500;
// A finger never stays perfectly still: small moves still count as a press.
const MOVE_TOLERANCE_PX = 10;

interface UseLongPressOptions {
  thresholdMs?: number;
  /** When false no handlers are returned, so the element behaves normally. */
  enabled?: boolean;
}

/**
 * Pointer handlers that call `onLongPress` once the pointer has been held down for
 * `thresholdMs` without moving (moving cancels it, so scrolling never triggers it).
 * Spread the result on the target element.
 */
export const useLongPress = (
  onLongPress: () => void,
  { thresholdMs = DEFAULT_THRESHOLD_MS, enabled = true }: UseLongPressOptions = {}
) => {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    origin.current = null;
  }, []);

  useEffect(() => cancel, [cancel]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      cancel();
      origin.current = { x: event.clientX, y: event.clientY };
      timer.current = setTimeout(() => {
        cancel();
        onLongPress();
      }, thresholdMs);
    },
    [cancel, onLongPress, thresholdMs]
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!origin.current) return;
      const dx = event.clientX - origin.current.x;
      const dy = event.clientY - origin.current.y;
      if (Math.hypot(dx, dy) > MOVE_TOLERANCE_PX) cancel();
    },
    [cancel]
  );

  if (!enabled) return {};

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: cancel,
    onPointerLeave: cancel,
    onPointerCancel: cancel,
    // Avoid the browser/OS context menu competing with the long press
    onContextMenu: (event: React.MouseEvent) => event.preventDefault(),
  };
};
